// What the browser reports about the display. These are self-reports, not measurements:
// colorDepth 30 or dynamic-range: high do not prove 10 bits reach the panel. Chrome reports
// colorDepth 30 only for a screen with HDR (EDR) headroom, so a 10-bit monitor in SDR says 24;
// the screen check records it and does not judge by it (backend/vtlite/qualify.py).

export interface ScreenReport {
  label: string | null;
  isInternal: boolean | null;
  isPrimary: boolean | null;
  widthCss: number;
  heightCss: number;
  devicePixelRatio: number;
  widthPx: number;
  heightPx: number;
  colorDepth: number;
}

export interface ProbeReport {
  userAgent: string;
  screen: ScreenReport;
  dynamicRangeHigh: boolean;
  colorGamut: "srgb" | "p3" | "rec2020";
  webgl2: boolean;
  /** A float16 (RGBA16F) drawing buffer could actually be allocated. */
  drawingBufferStorage: boolean;
  refreshHz: number | null;
  measuredAt: string;
}

interface ScreenDetailedLike {
  label?: string;
  isInternal?: boolean;
  isPrimary?: boolean;
  width: number;
  height: number;
  devicePixelRatio?: number;
  colorDepth: number;
}

export function currentScreenReport(detail?: ScreenDetailedLike | null): ScreenReport {
  const s: ScreenDetailedLike = detail ?? window.screen;
  const dpr = detail?.devicePixelRatio ?? window.devicePixelRatio;
  return {
    label: detail?.label ?? null,
    isInternal: detail?.isInternal ?? null,
    isPrimary: detail?.isPrimary ?? null,
    widthCss: s.width,
    heightCss: s.height,
    devicePixelRatio: dpr,
    widthPx: Math.round(s.width * dpr),
    heightPx: Math.round(s.height * dpr),
    colorDepth: s.colorDepth,
  };
}

export function measureRefreshHz(frames = 90): Promise<number | null> {
  return new Promise((resolve) => {
    const times: number[] = [];
    const tick = (t: number) => {
      times.push(t);
      if (times.length < frames) requestAnimationFrame(tick);
      else {
        const d = times.slice(1).map((v, i) => v - times[i]!).sort((a, b) => a - b);
        const med = d[Math.floor(d.length / 2)];
        resolve(med && med > 0 ? Math.round((1000 / med) * 100) / 100 : null);
      }
    };
    requestAnimationFrame(tick);
  });
}

export async function probe(detail?: ScreenDetailedLike | null): Promise<ProbeReport> {
  const gamut = matchMedia("(color-gamut: rec2020)").matches ? "rec2020"
    : matchMedia("(color-gamut: p3)").matches ? "p3" : "srgb";
  let webgl2 = false;
  let dbs = false;
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2") as (WebGL2RenderingContext & {
      drawingBufferStorage?: (f: number, w: number, h: number) => void; drawingBufferFormat?: number;
    }) | null;
    webgl2 = !!gl;
    if (gl && typeof gl.drawingBufferStorage === "function") {
      // either extension makes RGBA16F renderable (GPUs differ in which they offer);
      // the renderer asks for both too
      gl.getExtension("EXT_color_buffer_half_float");
      gl.getExtension("EXT_color_buffer_float");
      try {
        gl.drawingBufferStorage(gl.RGBA16F, 4, 4);
        dbs = gl.getError() === gl.NO_ERROR && gl.drawingBufferFormat === gl.RGBA16F;
      } catch {
        dbs = false;
      }
    }
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    /* no WebGL */
  }
  return {
    userAgent: navigator.userAgent,
    screen: currentScreenReport(detail),
    dynamicRangeHigh: matchMedia("(dynamic-range: high)").matches,
    colorGamut: gamut,
    webgl2,
    drawingBufferStorage: dbs,
    refreshHz: await measureRefreshHz(),
    measuredAt: new Date().toISOString(),
  };
}

export interface ScreenDetailsResult {
  screens: ScreenDetailedLike[];
  current: ScreenDetailedLike;
  /** The live ScreenDetails object (fires "currentscreenchange"). */
  target: EventTarget & { currentScreen?: ScreenDetailedLike };
}

let lastDetails: ScreenDetailsResult | null = null;

/** The most recent successful getScreenDetails() in this window, if any. */
export function knownScreenDetails(): ScreenDetailsResult | null {
  return lastDetails;
}

/** Window Management API: list screens (asks permission once). Null when unsupported or denied. */
export async function screenDetails(): Promise<ScreenDetailsResult | null> {
  const w = window as unknown as { getScreenDetails?: () => Promise<EventTarget & { screens: ScreenDetailedLike[]; currentScreen: ScreenDetailedLike }> };
  if (typeof w.getScreenDetails !== "function") return null;
  try {
    const d = await w.getScreenDetails();
    lastDetails = { screens: d.screens, current: d.currentScreen, target: d };
    return lastDetails;
  } catch {
    return null;
  }
}
